import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import './MusicReflectionsPage.css';

export interface ReflectionSong { id: number; song_id: number; title: string; placement: string }
interface Reflection { date: string; title: string; songs: ReflectionSong[]; selected_ids: number[]; comment: string; revision: number }
async function publicRequest(path: string, body?: unknown) {
  const response = await fetch(`${API_BASE_URL}/music-reflections/${path}`, {
    method: body === undefined ? 'GET' : 'POST', headers: { 'Content-Type': 'application/json' },
    referrerPolicy: 'no-referrer', ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(typeof data.error === 'string' ? data.error : 'Please try again.');
  return data;
}
const dateLabel = (date: string) => new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

export default function MusicReflectionsPage() {
  const { action } = useParams<{ action: string }>();
  const [token] = useState(() => window.location.hash.slice(1));
  const [info, setInfo] = useState<{ church: string; service_time: string; require_approval: boolean; signup_open?: boolean } | null>(null);
  const [reflection, setReflection] = useState<Reflection | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [completed, setCompleted] = useState(false);
  const load = async () => {
    setError(''); setBusy(true);
    try {
      if (!action) setInfo(await publicRequest('signup-info'));
      else if (action === 'respond') setReflection(await publicRequest('open', { token }));
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };
  useEffect(() => { void load(); }, [action, token]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); if (busy) return;
    setBusy(true); setError(''); setMessage('');
    try {
      if (!action) {
        const result = await publicRequest('signup', { name, email, consent });
        setMessage(result.message);
      } else if (action === 'confirm') {
        const result = await publicRequest('confirm', { token });
        setMessage(result.status === 'pending' ? 'Email confirmed. Jack will review your signup before weekly emails begin.' : 'You are signed up for weekly Sunday Music Reflections.');
      } else if (action === 'unsubscribe') {
        setMessage((await publicRequest('unsubscribe', { token })).message);
      } else if (reflection) {
        const result = await publicRequest('answer', { token, selected_ids: reflection.selected_ids, comment: reflection.comment, revision: reflection.revision });
        setReflection({ ...reflection, revision: result.revision });
        setCompleted(true);
        setMessage('Your reflection is saved. Thank you. You can close this tab.');
        if (window.opener || window.history.length <= 1) window.close();
        else window.history.back();
      }
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };
  return <main className="music-reflections public-reflections">
    <a href="/">Church Music</a>
    <h1>Sunday Music Reflections</h1>
    {error && <div className="alert alert-danger" role="alert">{error}
      {!reflection && !info && <button className="btn btn-link" disabled={busy} onClick={() => void load()}>Try again</button>}
    </div>}
    {message && <div className="alert alert-success" role="status">{message}</div>}
    {busy && !reflection && !info && <p role="status">Loading…</p>}
    {!action && info?.signup_open === false && <p>Signup is closed while Jack tests Sunday Music Reflections.</p>}
    {!action && info && info.signup_open !== false && !message && <form onSubmit={submit}>
      <p>Sign up for a weekly email about the music at {info.church}’s {info.service_time} Sunday service.</p>
      <p>Your responses, including your name, will be visible only to Jack.</p>
      <label htmlFor="reflection-name">Name</label>
      <input id="reflection-name" className="form-control" autoComplete="name" value={name} onChange={e => setName(e.target.value)} required maxLength={120} disabled={busy} />
      <label htmlFor="reflection-email">Email</label>
      <input id="reflection-email" className="form-control" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} required maxLength={254} disabled={busy} />
      <label className="reflection-choice"><input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} required disabled={busy} />
        <span>Email me a weekly reflection link. I can unsubscribe from any weekly email.</span></label>
      <button className="btn btn-primary" disabled={busy || !consent}>Sign up by email</button>
    </form>}
    {(action === 'confirm' || action === 'unsubscribe') && !message && <form onSubmit={submit}>
      <p>{action === 'confirm' ? 'Confirm that you would like to receive weekly reflection emails.' : 'Stop receiving weekly reflection emails.'}</p>
      <button className="btn btn-primary" disabled={busy || !token}>{action === 'confirm' ? 'Confirm signup' : 'Unsubscribe'}</button>
    </form>}
    {action === 'respond' && reflection && !completed && <form onSubmit={submit}>
      <h2>{dateLabel(reflection.date)}</h2><p>{reflection.title}</p>
      <p>Only Jack can see your responses and your name.</p>
      <fieldset disabled={busy}>
        <legend>Which of these songs drew you in spiritually or musically?</legend>
        <p>Choose any that apply. You can leave this blank.</p>
        {reflection.songs.map(song => <label className="reflection-choice" key={song.id}>
          <input type="checkbox" checked={reflection.selected_ids.includes(song.id)} onChange={e => {
            setMessage(''); setReflection({ ...reflection, selected_ids: e.target.checked
              ? [...reflection.selected_ids, song.id] : reflection.selected_ids.filter(id => id !== song.id) });
          }} /><span><strong>{song.title}</strong><small>{song.placement}</small></span>
        </label>)}
        <label htmlFor="reflection-comment">Please share how any of these songs spoke to you during the service.</label>
        <textarea id="reflection-comment" className="form-control" rows={5} maxLength={5000} value={reflection.comment}
          onChange={e => { setMessage(''); setReflection({ ...reflection, comment: e.target.value }); }} />
        <p className="text-muted">Optional</p>
        <button className="btn btn-primary" type="submit">{busy ? 'Saving…' : 'Save reflection'}</button>
      </fieldset>
    </form>}
    {action && !['confirm', 'unsubscribe', 'respond'].includes(action) && <p>This page is not available.</p>}
  </main>;
}
